import { useContext } from 'react';
import { useLingui } from '@lingui/react';
import { Divider, Group, Text as MantineText, Text } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { ViolationContext } from '../../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../../Service/LicenceS';
import SkeletonItems from '../../../../Atoms/SkeletonItems';
import EmptyText from '../../../../Atoms/EmptyText';
import BookitupTable from '../../../../Atoms/BookitupTable';
import { ExternalSubscription } from '../../../../Types/CalDavT';
import { useExternalSubscriptions } from '../../../../Service/hooks/useExternalSubscriptions';
import { BookitupTutorialButton } from '../../../../Atoms/BookitupTutorialIcon';
import BookitupAddIcon from '../../../../Atoms/BookitupAddIcon';
import ExternalCalendarItem from './ExternalCalendarItem';
import { openCreateExternalCalModal, openEditExternalCalModal } from './ExternalSubscriptionModals';
import { ThemeText } from '../../../../Atoms/ThemeText';

const ExternalSubscriptions = () => {
  const { i18n } = useLingui();
  const { ref, width } = useElementSize();
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { loading, subscriptions, handlers } = useExternalSubscriptions();
  const { append, applyWhere } = handlers;
  const { length } = subscriptions;
  const columnsVisibility = {
    showCalUrl: width > 500,
    showClashesIcon: width > 700,
  };

  const onCreate = (iCal: ExternalSubscription) => {
    append(iCal);
    openEditExternalCalModal({
      subscription: iCal,
      onChange: (sub) => {
        applyWhere(
          (item) => item.id === sub.id,
          () => sub,
        );
      },
    });
  };

  const addPrivateCalendar = () => {
    const addCalendar = () => openCreateExternalCalModal({ onCreate });
    if (length > 0) {
      executeWithLicenceCheck(LicenceS.Restrictions.PRIVATE_CALENDAR, addCalendar);
    } else {
      addCalendar();
    }
  };

  return (
    <div ref={ref}>
      <ThemeText weight={600}>{i18n._('external-subscription.headline')}</ThemeText>
      <MantineText size="sm" color="dimmed" my="xs">
        {i18n._('external-subscription.description')}
      </MantineText>
      <BookitupTutorialButton
        videoUrl="https://app.bookitup.de/academy/channels/automatic-responses?episodeId=faiabsidch"
        name="ConnectExternalCalendar"
        label="Externer Kalender verbinden"
      />
      <Divider my="xl" />
      <Group position="apart">
        <ThemeText size="lg" weight={600} mb="xl">
          Kalender
        </ThemeText>
        <BookitupAddIcon onClick={addPrivateCalendar} />
      </Group>

      <BookitupTable>
        <thead>
          <tr>
            <th style={{ width: 50 }}> Status </th>
            {columnsVisibility.showClashesIcon && <th style={{ width: 100 }}>Kollisionsprüfung</th>}
            <th style={{ width: 50 }}> </th>
            <th style={{ width: 200 }}>Name</th>
            {columnsVisibility.showCalUrl && <th> Kalender-URL</th>}
            <th> </th>
          </tr>
        </thead>
        <tbody>
          {length > 0 &&
            subscriptions.map((sub, idx) => (
              <ExternalCalendarItem
                key={sub.id}
                index={idx}
                handlers={handlers}
                externalSubscription={sub}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...columnsVisibility}
              />
            ))}
        </tbody>
      </BookitupTable>
      {loading && <SkeletonItems />}
      {!loading && length === 0 && <EmptyText mt="xs" ml="xs" />}
    </div>
  );
};

export default ExternalSubscriptions;
